import React, { useEffect, useMemo, useState } from 'react';
import { Upload, X, Loader2, Download, MessageSquare, Sparkles } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import ConfidenceBadge from '../common/ConfidenceBadge';
import RangeBar from '../common/RangeBar';
import { CHAT_ENABLED, openChatWidget } from '../common/ChatWidget';

// Feature flag: la tab esiste solo se REACT_APP_STUDIO_ENABLED === 'true'.
// Stesso pattern di CHAT_ENABLED (ChatWidget.js).
export const STUDIO_ENABLED = process.env.REACT_APP_STUDIO_ENABLED === 'true';

// Descrittori tessili per il preset "cambia materiale" (tie-in LCA, vedi
// PROGETTO-DESIGN-STUDIO.md §5.3). Le fibre senza descrittore specifico usano
// FALLBACK_HINT. Da rifinire in Fase 3 (pilota Alessia) sui risultati reali.
const FIBER_RENDER_HINTS = {
  'Organic Cotton': 'soft matte weave, natural drape, gentle texture',
  'Cotton': 'soft matte weave, natural drape',
  'Recycled Cotton': 'soft matte weave with slightly irregular texture, natural drape',
  'Linen (Flax)': 'natural slub texture, matte finish, soft drape with slight wrinkles',
  'Hemp': 'coarse visible weave, sturdy matte canvas look',
  'Recycled Polyester': 'smooth surface with a slight sheen, crisp drape, no visible weave',
  'Polyester': 'smooth surface with a slight sheen, crisp drape, no visible weave',
  'Wool': 'soft brushed surface, warm matte texture, structured drape',
  'Merino Wool': 'fine soft brushed surface, warm matte texture, structured drape',
  'Cashmere': 'ultra-soft brushed surface, warm matte texture, fine loft',
  'Mulberry Silk': 'fluid drape, luminous sheen, fine smooth surface',
  'Wild Silk (Tussah)': 'fluid drape, subtle natural sheen, slightly slubbed surface',
  'Viscose': 'soft fluid drape, subtle sheen, smooth surface',
  'Rayon/Viscose': 'soft fluid drape, subtle sheen, smooth surface',
  'Tencel Lyocell': 'silky smooth surface, fluid drape, subtle natural sheen',
  'Nylon/Polyamide': 'smooth technical surface, slight sheen, crisp drape',
  'Recycled Nylon': 'smooth technical surface, slight sheen, crisp drape',
  'Elastane/Spandex': 'smooth stretch surface, close-fitting drape, slight sheen',
  'Leather': 'smooth grained natural surface, structured drape, matte to semi-gloss finish',
};
const FALLBACK_HINT = 'typical drape, texture and finish for this fiber';

const MAX_DIMENSION = 1536; // lato lungo, px — vedi PROGETTO-DESIGN-STUDIO.md §4
const JPEG_QUALITY = 0.85;
const MAX_SOURCE_FILE_BYTES = 10 * 1024 * 1024; // 10MB, prima del resize

const IMPACT_METRICS = [
  { key: 'GHG Emissions (kg CO2e/kg)', label: 'GHG Emissions', unit: 'kg CO2e/kg', tile: 'bg-red-50 border-red-200 text-red-700' },
  { key: 'Water Consumption (L/kg)', label: 'Water', unit: 'L/kg', tile: 'bg-blue-50 border-blue-200 text-blue-700' },
  { key: 'Energy Use (MJ/kg)', label: 'Energy', unit: 'MJ/kg', tile: 'bg-purple-50 border-purple-200 text-purple-700' },
];

function buildPrompt(fiber, hasSourceImage) {
  const hint = FIBER_RENDER_HINTS[fiber] || FALLBACK_HINT;
  if (hasSourceImage) {
    return (
      `Change the fabric of this garment to ${fiber}: ${hint}. ` +
      `Keep the cut, silhouette, colours, pose and background exactly unchanged.`
    );
  }
  return `Flat lay photo of a ${fiber} fabric swatch: ${hint}. Soft natural light, close-up texture.`;
}

// Resize lato client su <canvas> prima dell'upload (payload sotto controllo,
// meno token in input al provider) — vedi PROGETTO-DESIGN-STUDIO.md §4.
function resizeImageFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const img = new window.Image();
      img.onerror = () => reject(new Error('Could not decode this image.'));
      img.onload = () => {
        const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        resolve({ dataUrl, base64: dataUrl.split(',')[1] || '', mimeType: 'image/jpeg' });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const DesignStudio = ({ materials }) => {
  const fiberOptions = useMemo(
    () =>
      Array.from(new Set((materials || []).map((m) => m['Material Name']).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [materials]
  );

  const [fiber, setFiber] = useState('');
  const [sourceImage, setSourceImage] = useState(null); // { dataUrl, base64, mimeType } | null
  const [prompt, setPrompt] = useState('');
  const [autoPrompt, setAutoPrompt] = useState('');
  const [resultImage, setResultImage] = useState(null); // data URL
  const [resultFiber, setResultFiber] = useState(''); // fibra usata per il risultato mostrato
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  // Default: prima fibra disponibile, preferendo Organic Cotton se presente.
  useEffect(() => {
    if (fiber || fiberOptions.length === 0) return;
    setFiber(fiberOptions.includes('Organic Cotton') ? 'Organic Cotton' : fiberOptions[0]);
  }, [fiber, fiberOptions]);

  // Il preset segue la fibra scelta finché l'utente non modifica il testo a mano.
  useEffect(() => {
    if (!fiber) return;
    const preset = buildPrompt(fiber, Boolean(sourceImage));
    setPrompt((prev) => (prev === '' || prev === autoPrompt ? preset : prev));
    setAutoPrompt(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiber, sourceImage]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrorMsg('Please choose an image file (JPEG, PNG…).');
      return;
    }
    if (file.size > MAX_SOURCE_FILE_BYTES) {
      setErrorMsg(`File too large. Maximum size is ${MAX_SOURCE_FILE_BYTES / 1024 / 1024}MB.`);
      return;
    }
    try {
      const resized = await resizeImageFile(file);
      setSourceImage(resized);
      setResultImage(null);
      setErrorMsg(null);
    } catch (err) {
      setErrorMsg(err.message || 'Could not read this image. Please try another file.');
    }
  };

  const clearSourceImage = () => {
    setSourceImage(null);
    setResultImage(null);
    setZoomOpen(false);
  };

  const handleGenerate = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt || loading) return;
    setLoading(true);
    setErrorMsg(null);
    setZoomOpen(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Session expired: please log in again.');

      const resp = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          prompt: trimmedPrompt,
          ...(sourceImage ? { image_base64: sourceImage.base64, mime_type: sourceImage.mimeType } : {}),
        }),
      });

      if (!resp.ok) {
        let msg = `Service error (${resp.status}).`;
        try {
          const j = await resp.json();
          if (j?.error) msg = j.error;
        } catch (_) {
          /* body non JSON */
        }
        throw new Error(msg);
      }

      const data = await resp.json();
      if (!data?.image) throw new Error('The image service returned an unexpected response.');
      setResultImage(`data:${data.mime_type || 'image/png'};base64,${data.image}`);
      setResultFiber(fiber);
    } catch (err) {
      setErrorMsg(err.message || 'Generation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const impactDetail = useMemo(
    () => (materials || []).find((m) => m['Material Name'] === resultFiber) || null,
    [materials, resultFiber]
  );
  const hasImpactData = impactDetail && IMPACT_METRICS.some((m) => impactDetail[m.key]);

  if (!STUDIO_ENABLED) return null;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-green-600" />
          <h2 className="text-xl font-bold text-gray-900">Design Studio</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Visualize a garment in a different fiber, or generate a fabric swatch — connected to the
          sustAId dataset so you can see the environmental trade-off of your choice.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonna sinistra: input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Garment photo (optional)</label>
              {sourceImage ? (
                <div className="relative inline-block">
                  <img
                    src={sourceImage.dataUrl}
                    alt="Uploaded garment"
                    className="max-h-56 rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={clearSourceImage}
                    className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-gray-900"
                    title="Remove photo"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="relative group">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    id="design-studio-upload"
                  />
                  <label
                    htmlFor="design-studio-upload"
                    className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-8 text-gray-500 hover:border-green-400 hover:text-green-600 cursor-pointer transition-colors"
                  >
                    <Upload className="w-6 h-6" />
                    <span className="text-sm">Upload a garment photo or sketch to change its fabric</span>
                    <span className="text-xs text-gray-400">No photo? Generate a fabric swatch instead.</span>
                  </label>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="design-studio-fiber" className="block text-sm font-medium text-gray-700 mb-1">
                Fiber
              </label>
              <select
                id="design-studio-fiber"
                value={fiber}
                onChange={(e) => setFiber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              >
                {fiberOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="design-studio-prompt" className="block text-sm font-medium text-gray-700 mb-1">
                Prompt (edit if you like)
              </label>
              <textarea
                id="design-studio-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              />
            </div>

            {errorMsg && <p className="text-sm text-red-600">{errorMsg}</p>}

            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading || !prompt.trim()}
              className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                loading || !prompt.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generating… (usually 5-20s)</span>
                </>
              ) : (
                <span>{sourceImage ? 'Change fabric' : 'Generate swatch'}</span>
              )}
            </button>
          </div>

          {/* Colonna destra: risultato */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Result</label>
            <div className="border border-gray-200 rounded-lg bg-gray-50 min-h-[16rem] flex items-center justify-center p-3">
              {resultImage ? (
                <img
                  src={resultImage}
                  alt={`Generated ${resultFiber} variant`}
                  className="max-h-80 rounded cursor-zoom-in"
                  title="Click to enlarge"
                  onClick={() => setZoomOpen(true)}
                />
              ) : (
                <p className="text-sm text-gray-400 text-center px-6">
                  {loading ? 'Generating your image…' : 'Your result will appear here.'}
                </p>
              )}
            </div>

            {resultImage && (
              <>
                <p className="text-xs text-gray-500">AI-generated visualization — indicative only.</p>
                <a
                  href={resultImage}
                  download={`sustaid-design-studio-${(resultFiber || 'result').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </a>
              </>
            )}

            {hasImpactData && (
              <div className="pt-2">
                <p className="text-xs font-medium text-gray-600 mb-2">
                  Environmental impact of {resultFiber} (sustAId open dataset):
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {IMPACT_METRICS.map((metric) =>
                    impactDetail[metric.key] ? (
                      <div key={metric.key} className={`p-2 rounded-lg border-2 text-center ${metric.tile}`}>
                        <div className="text-xs font-semibold uppercase leading-tight">{metric.label}</div>
                        <div className="text-xs font-bold text-gray-900 mt-0.5 flex items-center justify-center gap-1 flex-wrap">
                          <span>
                            {impactDetail[metric.key]} {metric.unit}
                          </span>
                          <ConfidenceBadge
                            level={impactDetail[`${metric.key} Confidence`]}
                            sources={impactDetail[`${metric.key} Sources`]}
                          />
                        </div>
                        <RangeBar
                          min={impactDetail[`${metric.key} Min`]}
                          median={impactDetail[metric.key]}
                          max={impactDetail[`${metric.key} Max`]}
                        />
                      </div>
                    ) : null
                  )}
                </div>
                {CHAT_ENABLED && (
                  <button
                    type="button"
                    onClick={() =>
                      openChatWidget(`Tell me more about the environmental impact of ${resultFiber}`)
                    }
                    className="mt-2 flex items-center gap-1.5 text-xs text-green-700 hover:text-green-800 hover:underline"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>Ask the chat about {resultFiber}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {zoomOpen && resultImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 cursor-zoom-out"
          onClick={() => setZoomOpen(false)}
        >
          <button
            type="button"
            onClick={() => setZoomOpen(false)}
            aria-label="Close"
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={resultImage}
            alt={`Generated ${resultFiber} variant (enlarged)`}
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default DesignStudio;
